import axios from "axios";
import { redirectFunc } from "../../utils/redirect";

import i18next from "i18next";

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { STATUS, tokenKey } from "../../utils/constants";

// The server envelope is response.data.errors[0].errorMessages[0]. Every hop of
// that path is optional: a request that never reached the server has no
// `response`, and that is the case we most want to tell the operator about.
const loyaltyErrorMessage = (err) => {
  if (!err?.response) return i18next.t("Common:network.bad");

  return (
    err?.response?.data?.errors?.[0]?.errorMessages?.[0] ||
    i18next.t("Common:error.label")
  );
};

export const checkLumiGiftcard = createAsyncThunk(
  "check_lumi_giftcard",
  async (giftcard, { fulfillWithValue, rejectWithValue }) => {
    const token = localStorage.getItem(tokenKey);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}LumiLoyaltyGiftCards/Redeem?giftCardCode=${giftcard}`,
        { headers: { Authorization: "Bearer " + token } },
      );

      const data = response.data.data;

      return fulfillWithValue(data);
    } catch (err) {
      redirectFunc({ ...err });

      return rejectWithValue(loyaltyErrorMessage(err));
    }
  },
);

export const getCustomerData = createAsyncThunk(
  "get_customer_data",
  async (
    { email, phoneNumber, amount },
    { fulfillWithValue, rejectWithValue },
  ) => {
    const token = localStorage.getItem(tokenKey);

    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_BASE_URL
        }Members/GetLoyaltyDetails?phoneNumber=${phoneNumber}&amount=${amount}${
          email ? `&email=${email}` : ""
        }`,
        { headers: { Authorization: "Bearer " + token } },
      );

      const customerData = response.data.data;

      return fulfillWithValue(customerData);
    } catch (err) {
      redirectFunc({ ...err });

      return rejectWithValue(loyaltyErrorMessage(err));
    }
  },
);

export const getMemberPlans = createAsyncThunk(
  "get_member_plans",
  async (_, { fulfillWithValue, rejectWithValue }) => {
    const token = localStorage.getItem(tokenKey);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}MembershipPlans/ListAll?page=1&pageSize=100`,
        { headers: { Authorization: "Bearer " + token } },
      );

      const memberPlans = response.data.data?.pagedList;

      return fulfillWithValue(memberPlans);
    } catch (err) {
      redirectFunc({ ...err });

      return rejectWithValue(loyaltyErrorMessage(err));
    }
  },
);

const initialState = {
  lumiGiftcardError: "",
  lumiGiftcardStatus: STATUS.IDLE,
  lumiGiftcardData: {},
  lumiRewardError: "",
  lumiRewardStatus: STATUS.IDLE,
  lumiRewardData: {},
  customerDataError: "",
  customerDataStatus: STATUS.IDLE,
  customerData: {},
  memberPlansError: "",
  memberPlansStatus: STATUS.IDLE,
  memberPlans: [],
};

export const loyaltySlice = createSlice({
  name: "loyalty",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(checkLumiGiftcard.pending, (state) => {
        state.lumiGiftcardError = "";
        state.lumiGiftcardStatus = STATUS.PENDING;
      })
      .addCase(checkLumiGiftcard.fulfilled, (state, action) => {
        state.lumiGiftcardError = "";
        state.lumiGiftcardStatus = STATUS.RESOLVED;
        state.lumiGiftcardData = action.payload;
      })
      .addCase(checkLumiGiftcard.rejected, (state, action) => {
        state.lumiGiftcardError = action.payload;
        state.lumiGiftcardStatus = STATUS.REJECTED;
      })
      .addCase(getCustomerData.pending, (state) => {
        state.customerDataError = "";
        state.customerDataStatus = STATUS.PENDING;
      })
      .addCase(getCustomerData.fulfilled, (state, action) => {
        state.customerDataError = "";
        state.customerDataStatus = STATUS.RESOLVED;
        state.customerData = action.payload;
      })
      .addCase(getCustomerData.rejected, (state, action) => {
        state.customerDataError = action.payload;
        state.customerDataStatus = STATUS.REJECTED;
      })
      .addCase(getMemberPlans.pending, (state) => {
        state.memberPlansError = "";
        state.memberPlansStatus = STATUS.PENDING;
      })
      .addCase(getMemberPlans.fulfilled, (state, action) => {
        state.memberPlansError = "";
        state.memberPlansStatus = STATUS.RESOLVED;
        state.memberPlans = action.payload;
      })
      .addCase(getMemberPlans.rejected, (state, action) => {
        state.memberPlansError = action.payload;
        state.memberPlansStatus = STATUS.REJECTED;
      });
  },
});

export const loyaltyReducer = loyaltySlice.reducer;
